import { wallOpeningOpening3, wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { PorchKeyMeasures } from "building-types/porches/porchKeyMeasuresTypes";
import { DEFAULT_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { PorchHeaderTrimAlignment, TrimJustification } from "building-types/settings/settingsTypesEnum";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import type { BRepViewEntity } from "building-types/views/viewEntityTypes";
import type { GeneratedWindowPlacement, WindowUnit } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { porchHeaderKeyMeasuresNew } from "doc/porch/porchKeyMeasures";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { brepViewEntityNew, faceArrayViewEntityNew } from "doc/utils/viewEntityTools";
import { aabb2New } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import { addConvexPlanesToBSPLeaf, rayIntersectionBSP } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clipFaces, clipRail, clipRails, clipRails3, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP, PlaneBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_Z_FIGHT } from "geom/core";
import { extrudeFaceAlongPolyLine3, extrudePolyLine2InZ, extrusionToBRep } from "geom/extrude";
import { CurveCurveRangeIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { joinPolyLine3Rails } from "geom/join";
import { line2IsParallel, line2PerpDistance } from "geom/line2";
import { offsetPolyLine2, offsetPolyLine2Variable } from "geom/offset";
import { planeNew, planeNewXY, planeNormal, planeProjectPolyLine3, polyLine2OnPlane } from "geom/plane";
import { planeBSPByPointAndNormal, planeBSPFlip, planeBSPFromPlane, planeBSPMoveAlongNormal } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2Concat,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Extend,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2FromAABB2,
  polyLine2MergeCollinearEdges,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2Rotate,
  polyLine2SetCW,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByTwoPoints,
  polyLine3Copy,
  polyLine3EdgeCount,
  polyLine3EdgeStart,
  polyLine3EdgeTangent,
  polyLine3Length,
  polyLine3MergeCollinearEdges,
  polyLine3OnPlane,
} from "geom/polyLine3";
import { ray2Project } from "geom/ray2";
import { vec3CrossNormalize, vec3Dot } from "geom/vec3";
import { pushMissingProfileReferenceIssue } from "../profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "../profileTools";

interface MakePorchHeadersResult {
  readonly porchHeaders: FaceArrayViewEntity[];
  readonly porchHeaderTrims: BRepViewEntity[];
}

export function makePorchHeaders(
  insideRoomBSP: NodeBSP | null,
  segment: PolyLine2,
  roofs: Table<GeneratedRoofSystem>,
  windowUnits: Table<WindowUnit>,
  bottomOfBeam: number,
  beamDepth: number,
  beamRoofRayCastDistance: number,
  keyMeasures: PorchKeyMeasures,
  porchSettings: PorchSettingsBase,
  exteriorTrimSettings: ExteriorTrimSettings,
  profileSource: ActiveProfileSource,
  issues?: BuildingGenerationIssue[],
): MakePorchHeadersResult {
  const porchHeaders: FaceArrayViewEntity[] = [];
  const porchHeaderTrims: BRepViewEntity[] = [];

  const {
    porchHeaderTrimProfile = exteriorTrimSettings.porchHeaderTrimProfile ?? ProfileExtrusionType.NONE,
    porchHeaderTrimAlignment = exteriorTrimSettings.porchHeaderTrimAlignment ?? PorchHeaderTrimAlignment.TOP_OF_HEADER,
    porchHeaderTrimJustification = exteriorTrimSettings.porchHeaderTrimJustification ?? TrimJustification.BELOW,
    porchHeaderTrimOffset = exteriorTrimSettings.porchHeaderTrimOffset ?? 0,
    porchHeaderTrimDepthOffset = exteriorTrimSettings.porchHeaderTrimDepthOffset ?? 0,
    porchHeaderTrimMaterial = exteriorTrimSettings.porchHeaderTrimMaterial,
    headerCasingThickness,
    headerCasingOffsetFromCenterLine,
    headerCasingMaterial,
  } = porchSettings;

  if (headerCasingThickness < DIST_EPSILON) {
    return { porchHeaders, porchHeaderTrims };
  }

  const beamExteriorOffset = headerCasingThickness / 2 + headerCasingOffsetFromCenterLine;
  const beamInteriorOffset = headerCasingThickness / 2 - headerCasingOffsetFromCenterLine;

  // build the beam clipping options
  const clipBeamOptions: ClipRailContext = {
    clippingNodes: [clippingNodeNew(insideRoomBSP)],
    coplanarsToFront: false,
  };

  const offsetSegment = offsetPolyLine2(segment, beamExteriorOffset);

  const [sx, sy] = polyLine2EdgeStart(offsetSegment, 0);
  const [stx, sty] = polyLine2EdgeTangent(offsetSegment, 0);
  const [ex, ey] = polyLine2EdgeEnd(offsetSegment, polyLine2EdgeCount(offsetSegment) - 1);
  const [etx, ety] = polyLine2EdgeTangent(offsetSegment, polyLine2EdgeCount(offsetSegment) - 1);

  const startExtension = getRayExtensionDistToRoofFaceBSP(
    roofs,
    sx,
    sy,
    sx - stx * beamRoofRayCastDistance,
    sy - sty * beamRoofRayCastDistance,
    bottomOfBeam + beamDepth,
    beamRoofRayCastDistance,
  );

  const endExtension = getRayExtensionDistToRoofFaceBSP(
    roofs,
    ex,
    ey,
    ex + etx * beamRoofRayCastDistance,
    ey + ety * beamRoofRayCastDistance,
    bottomOfBeam + beamDepth,
    beamRoofRayCastDistance,
  );

  const beamSegment = polyLine2Copy(segment);

  if (startExtension !== 0) {
    polyLine2ExtendStart(beamSegment, startExtension);
  }

  if (endExtension !== 0) {
    polyLine2ExtendEnd(beamSegment, endExtension);
  }

  const porchHeaderDescriptor = getActiveProfileDescriptor(
    profileSource,
    porchHeaderTrimProfile,
    DEFAULT_TRIM_PROFILE_DESCRIPTOR,
  );
  const exteriorTrimHandle: DocEntityHandle | undefined = exteriorTrimSettings.id
    ? { type: "SETTINGS", id: exteriorTrimSettings.id }
    : undefined;
  pushMissingProfileReferenceIssue(
    issues,
    profileSource,
    porchHeaderTrimProfile,
    exteriorTrimHandle,
    "porchHeaderTrimProfile",
  );
  const porchHeaderProfile = polyLine2Copy(getProfileExtrusionDescriptorProfile(porchHeaderDescriptor));
  const porchHeaderProfileAABB = polyLine2AABB2(porchHeaderProfile);
  const porchHeaderSize = aabb2Height(porchHeaderProfileAABB);
  const porchHeaderThickness = aabb2Width(porchHeaderProfileAABB);

  let porchHeaderHeightOffset = 0;
  let porchHeaderDepthOffset = porchHeaderTrimDepthOffset;
  if (porchHeaderTrimAlignment === PorchHeaderTrimAlignment.TOP_OF_HEADER) {
    if (porchHeaderTrimJustification === TrimJustification.ABOVE) {
      porchHeaderHeightOffset += beamDepth + porchHeaderTrimOffset;
      porchHeaderDepthOffset += headerCasingOffsetFromCenterLine + porchHeaderThickness / 2;
    } else {
      porchHeaderHeightOffset += beamDepth - porchHeaderSize - porchHeaderTrimOffset;
      porchHeaderDepthOffset += beamExteriorOffset;
    }
  } else {
    if (porchHeaderTrimJustification === TrimJustification.ABOVE) {
      porchHeaderHeightOffset += porchHeaderTrimOffset;
      porchHeaderDepthOffset += beamExteriorOffset;
    } else {
      porchHeaderHeightOffset += -porchHeaderSize - porchHeaderTrimOffset;
      porchHeaderDepthOffset += headerCasingOffsetFromCenterLine + porchHeaderThickness / 2;
    }
  }

  polyLine2Move(porchHeaderProfile, porchHeaderDepthOffset, -porchHeaderProfileAABB.minY);
  const archedPorchHeaderProfile = polyLine2Rotate(polyLine2Copy(porchHeaderProfile), -Math.PI / 2);

  const { trimmedBeamRails, archedBeamRails, porchHeaderRails, archedPorchHeaderRails } = trimBeamRailWithRoofOpenings(
    beamSegment,
    roofs,
    windowUnits,
    bottomOfBeam,
    beamDepth,
    beamInteriorOffset,
    beamExteriorOffset,
    porchHeaderHeightOffset,
    porchHeaderSize,
  );

  for (const trimmedBeamRail of trimmedBeamRails) {
    const plane = planeNewXY(0, 0, bottomOfBeam);
    const rail2 = polyLine2MergeCollinearEdges(polyLine2Copy(trimmedBeamRail));
    const rail = polyLine3OnPlane(rail2, plane);

    keyMeasures.headers.push(porchHeaderKeyMeasuresNew(rail, beamExteriorOffset + beamInteriorOffset, beamDepth));

    const roofTrimInnerProfile = offsetPolyLine2(trimmedBeamRail, -beamInteriorOffset);
    const roofTrimOuterProfile = offsetPolyLine2(trimmedBeamRail, beamExteriorOffset);
    polyLine2Reverse(roofTrimOuterProfile);

    // build the proch header
    const roofTrimExtProfile = polyLine2Concat(roofTrimInnerProfile, roofTrimOuterProfile, true);
    const roofTrim = extrusionToBRep(extrudePolyLine2InZ(roofTrimExtProfile, bottomOfBeam, beamDepth - DIST_Z_FIGHT));

    // clip the faces and add to the porch header faces
    const clippedFaces = clipFaces({
      faces: roofTrim.faces,
      options: clipBeamOptions,
    });

    porchHeaders.push(faceArrayViewEntityNew(clippedFaces, porchHeaderDescriptor, headerCasingMaterial));
  }

  const archedBeamProfile = polyLine2SetCW(
    polyLine2FromAABB2(aabb2New(-DIST_Z_FIGHT, -beamExteriorOffset, beamDepth - DIST_Z_FIGHT, beamInteriorOffset)),
  );

  for (const { rail, plane } of archedBeamRails) {
    if (!rail || !plane) {
      continue;
    }

    const [nX, nY, nZ] = planeNormal(plane);

    keyMeasures.headers.push(
      porchHeaderKeyMeasuresNew(
        polyLine3MergeCollinearEdges(polyLine3Copy(rail)),
        beamExteriorOffset + beamInteriorOffset,
        beamDepth,
      ),
    );

    const archedBeam = extrudeFaceAlongPolyLine3([archedBeamProfile], rail, nX, nY, nZ);
    porchHeaders.push(faceArrayViewEntityNew(archedBeam.faces, porchHeaderDescriptor, headerCasingMaterial));
  }

  if (porchHeaderTrimProfile !== ProfileExtrusionType.NONE && porchHeaderDescriptor) {
    const joinedPorchHeaderRails = joinPolyLine3Rails({
      segments: porchHeaderRails,
      searchBoxTolerance: 1,
      allowRevert: false,
      simplify: true,
    });

    for (const porchHeaderRail of joinedPorchHeaderRails) {
      const porchHeader = extrudeFaceAlongPolyLine3([porchHeaderProfile], porchHeaderRail, 0, 0, 1);
      porchHeaderTrims.push(brepViewEntityNew(porchHeader, undefined, porchHeaderDescriptor, porchHeaderTrimMaterial));
    }

    for (const { rail, plane } of archedPorchHeaderRails) {
      const [nX, nY, nZ] = planeNormal(plane);
      const porchHeader = extrudeFaceAlongPolyLine3([archedPorchHeaderProfile], rail, nX, nY, nZ);
      porchHeaderTrims.push(brepViewEntityNew(porchHeader, undefined, porchHeaderDescriptor, porchHeaderTrimMaterial));
    }
  }

  return {
    porchHeaders,
    porchHeaderTrims,
  };
}

interface ArchedBeamRail {
  rail: PolyLine3;
  plane: Plane;
}

function archedBeamRailNew(rail: PolyLine3, plane: Plane): ArchedBeamRail {
  return {
    rail,
    plane,
  };
}

function isBeamOverlapping(beamSegment: PolyLine2, endRail: PolyLine2, maxDistance: number): boolean {
  const [x0, y0] = polyLine2Start(endRail);
  const [x1, y1] = polyLine2End(endRail);
  const [tx, ty] = polyLine2EdgeTangent(endRail, 0);

  const [x2, y2] = polyLine2Start(beamSegment);
  const [x3, y3] = polyLine2End(beamSegment);

  if (!line2IsParallel(x0, y0, x1, y1, x2, y2, x3, y3)) {
    return false;
  }

  if (line2PerpDistance(x0, y0, x1, y1, x2, y2) > maxDistance + DIST_EPSILON) {
    return false;
  }

  const [px2, py2] = ray2Project(x0, y0, tx, ty, x2, y2);
  const [px3, py3] = ray2Project(x0, y0, tx, ty, x3, y3);

  const intersection = intersectLine2Line2(x0, y0, x1, y1, px2, py2, px3, py3);

  if (!intersection || !(intersection instanceof CurveCurveRangeIntersection)) {
    return false;
  }

  return true;
}

interface TrimBeamRailWithRoofOpeningsResult {
  readonly trimmedBeamRails: PolyLine2[];
  readonly archedBeamRails: ArchedBeamRail[];
  readonly porchHeaderRails: PolyLine3[];
  readonly archedPorchHeaderRails: ArchedBeamRail[];
}

function trimBeamRailWithRoofOpenings(
  beamRail: PolyLine2,
  roofs: Table<GeneratedRoofSystem>,
  windowUnits: Table<WindowUnit>,
  bottomOfBeam: number,
  beamDepth: number,
  beamInteriorOffset: number,
  beamExteriorOffset: number,
  porchHeaderHeightOffset: number,
  porchHeaderSize: number,
): TrimBeamRailWithRoofOpeningsResult {
  const basePlane = planeNewXY(0, 0, bottomOfBeam);
  const porchHeaderBasePlane = planeNewXY(0, 0, bottomOfBeam + porchHeaderHeightOffset);

  const archedBeamRails: ArchedBeamRail[] = [];
  const porchHeaderRails: PolyLine3[] = [];
  const archedPorchHeaderRails: ArchedBeamRail[] = [];
  let trimmedBeamRails = [beamRail];
  let trimmedPorchHeaderRails = [beamRail];

  const insertedWindows: { [key: string]: boolean } = {};

  const clipArchedRailsBSP = addConvexPlanesToBSPLeaf([planeBSPFromPlane(basePlane)]);
  const clipArchedRailsOptions: ClipRailContext = {
    clippingNodes: [clippingNodeNew(clipArchedRailsBSP)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  // helper function for creating a set of planes for a given window opening
  const getPlanesFromWindowOpening = (
    opening: WallOpening,
    sideOffset: number = 0,
    bottomOffset: number = 0,
    topOffset: number,
  ): PlaneBSP[] => {
    const { frontPlane, backPlane } = opening;
    const frontOutline3 = wallOpeningOpening3(opening, "FRONT", false);
    const frontBackOffset = Math.max(beamInteriorOffset, beamExteriorOffset);
    const frontPlaneBSP = planeBSPMoveAlongNormal(planeBSPFromPlane(frontPlane), frontBackOffset);
    const backPlaneBSP = planeBSPMoveAlongNormal(planeBSPFromPlane(backPlane), frontBackOffset);
    const { nX, nY, nZ } = frontPlaneBSP;

    const planes: PlaneBSP[] = [frontPlaneBSP, backPlaneBSP];
    assertIsNotNullOrUndefined(frontOutline3);
    const frontOutline2 = planeProjectPolyLine3(frontPlane, frontOutline3);
    const aabb = polyLine2AABB2(frontOutline2);
    const edges2 = polyLine2FromAABB2(aabb);

    if (!(polyLine2EdgeCount(edges2) === 4)) {
      console.assert(false);
    }

    const offsetEdges2 = offsetPolyLine2Variable(edges2, [bottomOffset, sideOffset, topOffset, sideOffset]);
    const edges3 = polyLine2OnPlane(frontPlane, offsetEdges2);
    const edgeCount = edges3 ? polyLine3EdgeCount(edges3) : 0;

    for (let i = 0; i < edgeCount; i++) {
      const [x0, y0, z0] = polyLine3EdgeStart(edges3, i);
      const [tx, ty, tz] = polyLine3EdgeTangent(edges3, i);
      const [edgePlaneNormalX, edgePlaneNormalY, edgePlaneNormalZ] = vec3CrossNormalize(tx, ty, tz, nX, nY, nZ);

      planes.push(planeBSPByPointAndNormal(x0, y0, z0, edgePlaneNormalX, edgePlaneNormalY, edgePlaneNormalZ));
    }

    return planes;
  };

  // helper function for trimming the beam rail with the gable end openings
  const trimWithSubRoofOpenings = (endRail: PolyLine2, windows: Table<GeneratedWindowPlacement>) => {
    let bsp: NodeBSP | null = null;
    const maxDistance = Math.max(beamInteriorOffset, beamExteriorOffset);

    // iterate beam segments
    for (let i = 0, l = polyLine2EdgeCount(beamRail); i < l; i++) {
      const beamSegment = polyLine2Edge(beamRail, i);

      // check overlap
      if (!isBeamOverlapping(beamSegment, endRail, maxDistance)) {
        continue;
      }

      const [tx, ty] = polyLine2EdgeTangent(beamSegment, 0);
      const [x, y] = polyLine2Start(beamSegment);
      const beamEdgePlane = planeNew(x, y, 0, tx, ty, 0, 0, 0, 1);

      const filteredWindows = Object.values(windows).filter((x) => x.descriptor.open);
      const [t2x, t2y] = polyLine2EdgeTangent(beamSegment, 0);
      const plane = planeNew(x, y, 0, 0, 0, 1, t2x, t2y, 0);

      // add the window arches to the list
      filteredWindows.forEach((window) => {
        const { id, opening } = window;
        const windowUnit = windowUnits[id];

        if (!windowUnit || insertedWindows[id]) {
          return;
        }

        insertedWindows[id] = true;

        assertIsNotNullOrUndefined(opening);
        const planOutline = wallOpeningPlanExtents(opening);

        const [x0, y0] = polyLine2EdgeStart(planOutline, 0);
        const [x1, y1] = polyLine2EdgeEnd(planOutline, 0);

        const planes = getPlanesFromWindowOpening(opening, 0, beamDepth, beamDepth);
        bsp = addConvexPlanesToBSPLeaf(planes, bsp);

        const porchHeaderPlanes = getPlanesFromWindowOpening(
          opening,
          porchHeaderHeightOffset,
          porchHeaderSize,
          porchHeaderHeightOffset + porchHeaderSize,
        );

        const porchHeaderBSP = addConvexPlanesToBSPLeaf(porchHeaderPlanes);
        const porchHeaderOptions: ClipRailContext = {
          clippingNodes: [clippingNodeNew(porchHeaderBSP)],
        };

        const tmpPorchHeaderRails: PolyLine2[] = [];
        for (const trimmedPorchHeaderRail of trimmedPorchHeaderRails) {
          for (let j = 0, lj = polyLine2EdgeCount(trimmedPorchHeaderRail); j < lj; j++) {
            const pl = polyLine2Edge(trimmedPorchHeaderRail, j);
            const [x2, y2] = polyLine2Start(pl);
            const [x3, y3] = polyLine2End(pl);

            if (line2IsParallel(x0, y0, x1, y1, x2, y2, x3, y3)) {
              clipRail({
                rail: pl,
                height: bottomOfBeam + porchHeaderHeightOffset,
                options: porchHeaderOptions,
                clippedRails: tmpPorchHeaderRails,
              });
            } else {
              tmpPorchHeaderRails.push(pl);
            }
          }
        }

        trimmedPorchHeaderRails = tmpPorchHeaderRails;

        const clipPorchHeaderArchedRailsBSP = addConvexPlanesToBSPLeaf([
          planeBSPFlip(planeBSPFromPlane(porchHeaderBasePlane)),
          ...porchHeaderPlanes,
        ]);
        const clipPorchHeaderRailsOptions: ClipRailContext = {
          clippingNodes: [clippingNodeNew(clipPorchHeaderArchedRailsBSP, true)],
          epsilon: MULTIGABLE_BSP_EPSILON,
        };

        // we add the arches trimmed by the base beam plane
        const trimmedPorchHeaderArches = clipRails3({
          rails: windowUnit.arches.map((x) =>
            polyLine3OnPlane(
              offsetPolyLine2(
                polyLine2Extend(planeProjectPolyLine3(beamEdgePlane, x), beamDepth, beamDepth),
                -porchHeaderHeightOffset,
              ),
              beamEdgePlane,
            ),
          ),
          options: clipPorchHeaderRailsOptions,
        });

        for (const archedTrimRail of trimmedPorchHeaderArches) {
          archedPorchHeaderRails.push(archedBeamRailNew(archedTrimRail, plane));
        }

        const trimmedArches = clipRails3({
          rails: windowUnit.arches.map((x) => polyLine3OnPlane(planeProjectPolyLine3(beamEdgePlane, x), beamEdgePlane)),
          options: clipArchedRailsOptions,
        });

        const trimmedArchesCount = trimmedArches.length;
        if (trimmedArchesCount === 0) {
          return;
        }

        for (const archedBeamRail of trimmedArches) {
          archedBeamRails.push(archedBeamRailNew(archedBeamRail, plane));
        }

        // add conection rails
        if (trimmedPorchHeaderArches.length > 0) {
          const firstTrimArchSegment = trimmedPorchHeaderArches[0];
          const lastTrimArchSegment = trimmedPorchHeaderArches[trimmedPorchHeaderArches.length - 1];

          const insideSegments = clipRail({
            rail: beamSegment,
            height: bottomOfBeam + porchHeaderHeightOffset,
            options: {
              clippingNodes: [clippingNodeNew(addConvexPlanesToBSPLeaf(porchHeaderPlanes), true)],
            },
          });

          if (insideSegments.length > 0) {
            const firstInsideSegment = insideSegments[0];
            const lastInsideSegment = insideSegments[insideSegments.length - 1];

            const [x0, y0] = polyLine2EdgeStart(firstInsideSegment, 0);
            const [x1, y1] = polyLine2EdgeEnd(lastInsideSegment, polyLine2EdgeCount(lastInsideSegment) - 1);

            const startTrimRail = polyLine3ByTwoPoints(
              x0,
              y0,
              bottomOfBeam + porchHeaderHeightOffset,
              firstTrimArchSegment[0],
              firstTrimArchSegment[1],
              bottomOfBeam + porchHeaderHeightOffset,
            );

            const endTrimRail = polyLine3ByTwoPoints(
              lastTrimArchSegment[lastTrimArchSegment.length - 3],
              lastTrimArchSegment[lastTrimArchSegment.length - 2],
              bottomOfBeam + porchHeaderHeightOffset,
              x1,
              y1,
              bottomOfBeam + porchHeaderHeightOffset,
            );

            if (polyLine3Length(startTrimRail) > DIST_EPSILON) {
              const [tx, ty, tz] = polyLine3EdgeTangent(startTrimRail, 0);
              if (vec3Dot(tx, ty, tz, t2x, t2y, 0) > 0) {
                porchHeaderRails.push(startTrimRail);
              }
            }

            if (polyLine3Length(endTrimRail) > DIST_EPSILON) {
              const [tx, ty, tz] = polyLine3EdgeTangent(endTrimRail, 0);
              if (vec3Dot(tx, ty, tz, t2x, t2y, 0) > 0) {
                porchHeaderRails.push(endTrimRail);
              }
            }
          }
        }

        const insideSegments = clipRail({
          rail: beamSegment,
          height: bottomOfBeam,
          options: {
            clippingNodes: [clippingNodeNew(addConvexPlanesToBSPLeaf(planes), true)],
          },
        });

        if (insideSegments.length > 0) {
          const firstInsideSegment = insideSegments[0];
          const lastInsideSegment = insideSegments[insideSegments.length - 1];

          const [x0, y0] = polyLine2EdgeStart(firstInsideSegment, 0);
          const [x1, y1] = polyLine2EdgeEnd(lastInsideSegment, polyLine2EdgeCount(lastInsideSegment) - 1);

          const firstArchSegment = trimmedArches[0];
          const lastArchSegment = trimmedArches[trimmedArchesCount - 1];

          const startRail = polyLine3ByTwoPoints(
            x0,
            y0,
            bottomOfBeam,
            firstArchSegment[0],
            firstArchSegment[1],
            bottomOfBeam,
          );

          const endRail = polyLine3ByTwoPoints(
            lastArchSegment[lastArchSegment.length - 3],
            lastArchSegment[lastArchSegment.length - 2],
            bottomOfBeam,
            x1,
            y1,
            bottomOfBeam,
          );

          if (polyLine3Length(startRail) > DIST_EPSILON) {
            const [tx, ty, tz] = polyLine3EdgeTangent(startRail, 0);
            if (vec3Dot(tx, ty, tz, t2x, t2y, 0) > 0) {
              archedBeamRails.push(archedBeamRailNew(startRail, plane));
            }
          }

          if (polyLine3Length(endRail) > DIST_EPSILON) {
            const [tx, ty, tz] = polyLine3EdgeTangent(endRail, 0);
            if (vec3Dot(tx, ty, tz, t2x, t2y, 0) > 0) {
              archedBeamRails.push(archedBeamRailNew(endRail, plane));
            }
          }
        }
      });
    }

    // trim the beam with the holes BSP
    const options: ClipRailContext = {
      clippingNodes: [clippingNodeNew(bsp)],
    };

    trimmedBeamRails = clipRails({
      rails: trimmedBeamRails,
      height: bottomOfBeam,
      options,
    });
  };

  // iterate all roof system
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    // only valid for multi-gable roof systems

    // iterate all sub-roofs from the current roof system
    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const { rails, startWindows, endWindows, roofVolume } = subRoofs[subRoofId];
      const { rectangularOverhangs } = roofVolume;
      const { startOverhang, endOverhang } = rectangularOverhangs;
      const { startRail, endRail } = rails;

      const basePlane = planeNewXY();
      const startRail2 = offsetPolyLine2(planeProjectPolyLine3(basePlane, startRail), -startOverhang);
      const endRail2 = offsetPolyLine2(planeProjectPolyLine3(basePlane, endRail), endOverhang);

      // trim the beam rail using the openings on both gable ends
      trimWithSubRoofOpenings(startRail2, startWindows);
      trimWithSubRoofOpenings(endRail2, endWindows);
    }
  }

  porchHeaderRails.push(...trimmedPorchHeaderRails.map((x) => polyLine2OnPlane(porchHeaderBasePlane, x)));

  return {
    trimmedBeamRails,
    archedBeamRails,
    porchHeaderRails,
    archedPorchHeaderRails,
  };
}

function getRayExtensionDistToRoofFaceBSP(
  roofs: Table<GeneratedRoofSystem>,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  topOfBeam: number,
  beamRoofRayCastDistance: number,
) {
  let roofIntersected = false;
  let minExtensionDist = beamRoofRayCastDistance + 1;
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const intersection = rayIntersectionBSP({
      node: roof.clipContext.roofsBSP,
      coplanarsToFront: true,
      x1,
      y1,
      z1: topOfBeam,
      x2,
      y2,
      z2: topOfBeam,
      epsilon: MULTIGABLE_BSP_EPSILON,
    });

    if (intersection?.entryPoint && intersection.entryPoint.nZ > DIST_EPSILON) {
      const extensionDist = intersection.entryPoint.t * beamRoofRayCastDistance;
      if (extensionDist < minExtensionDist) {
        roofIntersected = true;
        minExtensionDist = extensionDist;
      }
    }
  }
  return roofIntersected ? minExtensionDist : 0;
}
